// Structural constraint shape (matches search-parser-v2's ParsedConstraint) kept
// local so the web app can lower search results with this same function.
type ParsedConstraint = {
  field: string;
  op: string;
  values?: (string | number | boolean)[];
  primary_values?: string[];
};

export type BuyRequestFields = {
  productForm: string | null;
  strainQuery: string | null;
  species: "indica" | "sativa" | "hybrid" | null;
  environment: "indoor" | "outdoor" | "greenhouse" | "mixed_light" | null;
  minQuantityG: number | null;
  maxPricePerG: number | null;
  minThcPercent: number | null;
};

const ENV: Record<string, BuyRequestFields["environment"]> = {
  indoor: "indoor", outdoor: "outdoor", greenhouse: "greenhouse",
  mixed_light: "mixed_light", "mixed light": "mixed_light", "light dep": "mixed_light", "light_dep": "mixed_light",
};
const POSITIVE = new Set(["in", "eq", "ilike", "contains"]);

const first = (c: ParsedConstraint) => (c.primary_values?.[0] ?? c.values?.[0]);
const nums = (c: ParsedConstraint) => (c.values ?? []).map(Number).filter((n) => Number.isFinite(n));

/** Lowers search-engine constraints into buy-request fields. Negations are ignored (a request states what you want). */
export function lowerToBuyRequest(constraints: ParsedConstraint[], freeText?: string | null): BuyRequestFields {
  const out: BuyRequestFields = {
    productForm: null, strainQuery: null, species: null, environment: null,
    minQuantityG: null, maxPricePerG: null, minThcPercent: null,
  };
  for (const c of constraints) {
    const n = nums(c);
    const lo = c.op === "between" ? n[0] : n[0];
    const hi = c.op === "between" ? n[1] : n[0];
    switch (c.field) {
      case "product_form":
        if (POSITIVE.has(c.op) && first(c) != null) out.productForm ??= String(first(c));
        break;
      case "strain_name":
        if (POSITIVE.has(c.op) && first(c) != null) out.strainQuery ??= String(first(c)).replace(/%/g, "").trim() || null;
        break;
      case "species": {
        const v = String(first(c) ?? "").toLowerCase();
        if (POSITIVE.has(c.op) && (v === "indica" || v === "sativa" || v === "hybrid")) out.species ??= v;
        break;
      }
      case "meta.epp_environment":
      case "environment": {
        const v = ENV[String(first(c) ?? "").toLowerCase()];
        if (POSITIVE.has(c.op) && v) out.environment ??= v;
        break;
      }
      case "price_per_g_canonical":
        if (["lt", "lte", "eq", "around", "between"].includes(c.op) && Number.isFinite(hi)) out.maxPricePerG = +hi.toFixed(4);
        break;
      case "thc_percent":
        if (["gt", "gte", "eq", "around", "between"].includes(c.op) && Number.isFinite(lo)) out.minThcPercent = lo;
        break;
      case "quantity_grams":
        if (["gt", "gte", "eq", "around", "between"].includes(c.op) && Number.isFinite(lo)) out.minQuantityG = Math.round(lo);
        break;
    }
  }
  // Leftover words the engine could not place (usually a strain name) still name what was asked for.
  if (!out.strainQuery && freeText && freeText.trim()) out.strainQuery = freeText.trim().slice(0, 120);
  return out;
}
